import type { SortingStrategy } from '@dnd-kit/sortable';
import type { LayoutRect } from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';

// Vertical mirror of virtualized-horizontal-list-sorting-strategy.
// Needed because @dnd-kit's built-in verticalListSortingStrategy assumes every
// item is mounted, which is not true inside a virtualized (react-virtuoso) list.

const defaultScale = {
	scaleX: 1,
	scaleY: 1,
};

function getItemGap(
	layoutRects: LayoutRect[],
	index: number,
	activeIndex: number,
): number {
	const current = layoutRects[index];
	const prev = layoutRects[index - 1];
	const next = layoutRects[index + 1];

	if (!current) return 0;

	if (!prev && !next) return 0;

	if (activeIndex < index) {
		if (prev) {
			return current.offsetTop - (prev.offsetTop + prev.height);
		} if (next) {
			return next.offsetTop - (current.offsetTop + current.height);
		}
		return 0;
	}

	if (next) {
		return next.offsetTop - (current.offsetTop + current.height);
	} if (prev) {
		return current.offsetTop - (prev.offsetTop + prev.height);
	}
	return 0;
}

// eslint-disable-next-line import/prefer-default-export
export const virtualizedVerticalSortingStrategy: SortingStrategy = ({
	layoutRects,
	activeIndex,
	overIndex,
	index,
	activeNodeRect: fallbackActiveRect,
}): Transform | null => {
	const activeNodeRect = layoutRects[activeIndex] ?? fallbackActiveRect;

	if (!activeNodeRect) return null;

	const itemGap = getItemGap(layoutRects, index, activeIndex);

	// CASE 1: It's the active (dragged) item — move it to new position
	if (index === activeIndex) {
		const newIndexRect = layoutRects[overIndex];
		if (!newIndexRect) return null;

		let deltaY = newIndexRect.offsetTop - activeNodeRect.offsetTop;
		if (activeIndex < overIndex) {
			deltaY += newIndexRect.height - activeNodeRect.height;
		}

		return {
			x: 0,
			y: deltaY,
			...defaultScale,
		};
	}

	// CASE 2: Items between original and new position shift up or down
	if (index > activeIndex && index <= overIndex) {
		return {
			x: 0,
			y: -activeNodeRect.height - itemGap,
			...defaultScale,
		};
	}

	if (index < activeIndex && index >= overIndex) {
		return {
			x: 0,
			y: activeNodeRect.height + itemGap,
			...defaultScale,
		};
	}

	// CASE 3: All others stay in place
	return {
		x: 0,
		y: 0,
		...defaultScale,
	};
};
